import type { GenerateResponse, SseEvent } from '@hysys-uv/shared';
import { useCallback, useMemo, useState } from 'react';
import { CodePanel } from './components/CodePanel.js';
import { GeneratorForm } from './components/GeneratorForm.js';
import { InstructionsPanel } from './components/InstructionsPanel.js';
import { PitfallsPanel } from './components/PitfallsPanel.js';
import { streamGenerate } from './lib/api.js';

type TabKey = 'instructions' | 'pitfalls' | 'test-plan' | 'warnings';

type GenerationState =
  | { kind: 'idle' }
  | { kind: 'streaming'; partialJson: string }
  | { kind: 'done'; response: GenerateResponse }
  | { kind: 'error'; message: string };

export function App(): JSX.Element {
  const [state, setState] = useState<GenerationState>({ kind: 'idle' });
  const [tab, setTab] = useState<TabKey>('instructions');

  const handleGenerate = useCallback(async (description: string) => {
    setState({ kind: 'streaming', partialJson: '' });
    try {
      let partial = '';
      for await (const event of streamGenerate({ description }) as AsyncGenerator<SseEvent>) {
        if (event.type === 'delta') {
          partial += event.text;
          setState({ kind: 'streaming', partialJson: partial });
        } else if (event.type === 'error') {
          setState({ kind: 'error', message: event.message });
          return;
        } else if (event.type === 'done') {
          setState({ kind: 'done', response: event.response });
        }
      }
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const code = useMemo(() => {
    if (state.kind === 'done') return state.response.code;
    if (state.kind === 'streaming') return extractStreamingCode(state.partialJson);
    return '';
  }, [state]);

  return (
    <div className="min-h-screen flex flex-col bg-bg-base text-neutral-100">
      <Header />
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(360px,40%)_1fr] gap-4 p-4 max-w-[1600px] mx-auto w-full">
        <section className="bg-bg-panel border border-border-subtle rounded-lg p-4 flex flex-col">
          <GeneratorForm
            onGenerate={handleGenerate}
            disabled={state.kind === 'streaming'}
          />
        </section>

        <section className="bg-bg-panel border border-border-subtle rounded-lg flex flex-col min-h-[500px]">
          {state.kind === 'idle' && <EmptyState />}
          {state.kind === 'error' && (
            <ErrorState
              message={state.message}
              onRetry={() => setState({ kind: 'idle' })}
            />
          )}
          {(state.kind === 'streaming' || state.kind === 'done') && (
            <>
              <CodePanel code={code} streaming={state.kind === 'streaming'} />
              <Tabs
                active={tab}
                onChange={setTab}
                warningCount={state.kind === 'done' ? state.response.warnings.length : 0}
              />
              <div className="flex-1 overflow-auto p-4 border-t border-border-subtle">
                {state.kind === 'done' && <TabBody tab={tab} response={state.response} />}
                {state.kind === 'streaming' && (
                  <p className="text-neutral-500 text-sm">Generating…</p>
                )}
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Header(): JSX.Element {
  return (
    <header className="border-b border-border-subtle px-4 py-3">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight">
          <span className="text-accent">HYSYS</span> UV Generator
        </h1>
        <span className="text-xs text-neutral-500">v0 — MaterialStream only</span>
      </div>
    </header>
  );
}

function Footer(): JSX.Element {
  return (
    <footer className="border-t border-border-subtle px-4 py-3 text-xs text-neutral-500">
      <div className="max-w-[1600px] mx-auto">
        Built by Pushpesh Sharma · Not affiliated with AspenTech ·{' '}
        <a
          href="https://github.com/Pushpesh-31/hysys-uv-generator"
          className="text-accent hover:underline"
        >
          GitHub
        </a>
      </div>
    </footer>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-neutral-500">
      <p className="text-lg mb-2">Paste a description, get a User Variable.</p>
      <p className="text-sm">
        Try one of the sample prompts on the left, or write your own.
      </p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <p className="text-red-400 mb-4">{message}</p>
      <button type="button" className="btn-primary" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function Tabs({
  active,
  onChange,
  warningCount,
}: {
  active: TabKey;
  onChange: (tab: TabKey) => void;
  warningCount: number;
}): JSX.Element {
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'instructions', label: 'Attach instructions' },
    { key: 'pitfalls', label: 'Pitfalls' },
    { key: 'test-plan', label: 'Test plan' },
    { key: 'warnings', label: warningCount > 0 ? `Warnings (${warningCount})` : 'Warnings' },
  ];
  return (
    <nav className="flex border-t border-border-subtle px-4">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`tab ${active === t.key ? 'tab-active' : ''}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

function TabBody({ tab, response }: { tab: TabKey; response: GenerateResponse }): JSX.Element {
  if (tab === 'instructions') return <InstructionsPanel attachment={response.attachment} version={response.usedVersion} />;
  if (tab === 'pitfalls') return <PitfallsPanel pitfalls={response.pitfalls} />;
  if (tab === 'test-plan') return <TestPlanPanel response={response} />;
  return <WarningsPanel response={response} />;
}

function TestPlanPanel({ response }: { response: GenerateResponse }): JSX.Element {
  if (response.testPlan.length === 0) {
    return <p className="text-neutral-500 text-sm">No test plan provided.</p>;
  }
  return (
    <ol className="space-y-3">
      {response.testPlan.map((step) => (
        <li key={step.step} className="border border-border-subtle rounded-md p-3">
          <div className="text-xs text-accent mb-1">Step {step.step}</div>
          <div className="text-sm mb-2">{step.action}</div>
          <div className="text-xs text-neutral-400">
            <span className="text-neutral-500">Expected: </span>
            {step.expected}
          </div>
        </li>
      ))}
    </ol>
  );
}

function WarningsPanel({ response }: { response: GenerateResponse }): JSX.Element {
  if (response.warnings.length === 0) {
    return <p className="text-neutral-500 text-sm">No warnings.</p>;
  }
  return (
    <ul className="space-y-2">
      {response.warnings.map((w, i) => (
        <li
          key={i}
          className="border border-yellow-900/40 bg-yellow-900/10 rounded-md p-3 text-sm"
        >
          <div className="text-yellow-300 text-xs uppercase tracking-wide mb-1">{w.kind}</div>
          <div className="text-neutral-200">{w.message}</div>
        </li>
      ))}
    </ul>
  );
}

function extractStreamingCode(partialJson: string): string {
  const match = partialJson.match(/"code"\s*:\s*"((?:[^"\\]|\\.)*)/);
  if (!match || !match[1]) return '';
  try {
    return JSON.parse('"' + match[1] + '"');
  } catch {
    return match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }
}
